import { useState } from "react";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { claimHouseholdSlug } from "@/lib/api/members";
import { householdSlugSchema } from "@/lib/schemas";
import { toast } from "@/components/shell/Toaster";
import { normalizeUsername } from "@/lib/utils/slug";

type Props = {
  /**
   * Callback po pomyślnym claim slug. Parent może np. zamknąć Sheet
   * lub przejść do następnego kroku.
   */
  onClaimed?: (slug: string) => void;
  /**
   * Domyślna wartość draft — np. nazwisko rodziny gdy znamy z kontekstu.
   * Pusty string OK.
   */
  initialDraft?: string;
};

// Deterministyczne sufiksy proponowane gdy slug jest zajęty. Nie sprawdzamy
// ich dostępności live — to byłoby slug-enumeration (MED-10 audyt 2026-05-12).
// User klika sugestię → form wstawia ją do drafta → user klika "Zarezerwuj".
const SUFFIX_SUGGESTIONS = [
  (b: string) => `${b}2`,
  (b: string) => `${b}-rodzina`,
  (b: string) => `${b}-${new Date().getFullYear()}`,
];

/**
 * Form do claim pseudonima rodziny. Single-shot operacja (raz na życie HH).
 *
 * Po FAZIE 9 audytu 2026-05-12: NIE używamy `dryRun` żeby live sprawdzać
 * dostępność — pre-fix odpowiedź ujawniała globalnie czy slug jest zajęty
 * (slug enumeration). R0.6 audyt refresh 2026-05-13: real claim też zwraca
 * tylko generic `reason: "unavailable"` (zamiast rozróżniać "taken" vs
 * "already-set") + rate limit zacieśniony do 3/godz. Flow: user wpisuje →
 * klik "Zarezerwuj" → backend albo potwierdza claim, albo generic toast
 * "niedostępne" + sufiksy client-side jako sugestie.
 *
 * Live normalize: input transformuje "Kowalski" → "kowalski", "Łukasz P"
 * → "lukasz-p" w trakcie pisania (`normalizeUsername`).
 */
export function SlugClaimForm({ onClaimed, initialDraft = "" }: Props) {
  const [draft, setDraft] = useState(initialDraft);
  const [busy, setBusy] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [takenSuggestions, setTakenSuggestions] = useState<string[]>([]);

  const onSave = async () => {
    const trimmed = draft.trim();
    const parsed = householdSlugSchema.safeParse(trimmed);
    if (!parsed.success) {
      setValidationError("3-20 znaków: a-z, 0-9, myślnik. Bez kropek/spacji.");
      return;
    }
    setValidationError(null);
    setTakenSuggestions([]);
    setBusy(true);
    try {
      const res = await claimHouseholdSlug(parsed.data);
      if ("ok" in res && res.ok) {
        toast.success(`Pseudonim "${parsed.data}" zarezerwowany`);
        onClaimed?.(parsed.data);
      } else if ("ok" in res && !res.ok) {
        if (res.reason === "unavailable") {
          // R0.6 audyt refresh 2026-05-13: backend zwraca generic
          // "unavailable" zamiast rozróżnienia "taken" vs "already-set" —
          // anti-enumeration (atakujący nie odkryje czyje są slugi).
          // Pokazujemy client-side sufiks suggestions identycznie jak pre-fix
          // — user nadal może łatwo wybrać alternatywę.
          toast.error("Ta nazwa jest niedostępna — wybierz inną");
          const candidates = SUFFIX_SUGGESTIONS.map((fn) => fn(parsed.data))
            .filter((c) => householdSlugSchema.safeParse(c).success)
            .slice(0, 3);
          setTakenSuggestions(candidates);
        } else {
          toast.error("Nie udało się zarezerwować");
        }
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Błąd");
    } finally {
      setBusy(false);
    }
  };

  const pickSuggestion = (s: string) => {
    setDraft(s);
    setTakenSuggestions([]);
    setValidationError(null);
  };

  return (
    <div className="space-y-3">
      <input
        value={draft}
        onChange={(e) => {
          // Live transform — user widzi natychmiast finalny format.
          setDraft(normalizeUsername(e.target.value));
          setValidationError(null);
          setTakenSuggestions([]);
        }}
        placeholder="np. kowalski"
        maxLength={20}
        className="w-full h-11 rounded-[12px] border border-line bg-card px-3 text-sm focus:outline-none focus:border-accent"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
      />
      {validationError && <p className="text-xs text-danger">{validationError}</p>}
      {takenSuggestions.length > 0 && (
        <div>
          <p className="text-[11px] text-muted mb-1.5">Może to?</p>
          <div className="flex flex-wrap gap-1.5">
            {takenSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => pickSuggestion(s)}
                className="px-2.5 py-1 rounded-full bg-soft border border-line text-xs font-semibold hover:border-accent transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      <Button
        type="button"
        variant="accent"
        size="lg"
        block
        onClick={onSave}
        disabled={busy || draft.trim().length < 3}
      >
        <Lock className="h-4 w-4" />
        {busy ? "Zapisywanie…" : "Zarezerwuj na stałe"}
      </Button>
      <p className="text-[11px] text-muted">
        Po zarezerwowaniu nie da się zmienić — wybór jest finalny.
      </p>
    </div>
  );
}
